import React from 'react';
import { Knowledgebase, Prompt } from '../../../../Types';
import TabSection from './TabSection';
import TabHeadline from './TabHeadline';
import CheckIcon from '../../../Utility/CheckIcon';
import InfoButton from '../../../Utility/InfoButton';
import { PaginationWrapper } from '../../../Utility/PaginationWrapper';
import { PaginationListType } from '../../../../Types/PaginationParams';
import { knowledgebaseService } from '../../../../Services/knowledgebaseService';
import useSearchFilter from '../../../../Hooks/useSearchFilter';
import SearchFilter from '../../../Utility/SearchFilter';

interface KnowledgebaseProps {
    userPrompt: boolean;
    prompt: Prompt;
    i18n: any;
    PromptActions: any;
}

function KnowledgebaseTab({ userPrompt, prompt, i18n, PromptActions }: KnowledgebaseProps) {
    const { query, localQuery, searchActions } = useSearchFilter({
        initialQuery: null,
    });

    return (
        <div className="w-full">
            <TabHeadline
                title={i18n.translate({
                    id: 'tabs.knowledgebase.title',
                    sourceName: 'Prompt',
                })}
            />
            <InfoButton configKey="knowledgebase" />

            {!userPrompt && (
                <TabSection
                    title={i18n.translate({
                        id: 'tabs.knowledgebase.section.knowledgebase.title',
                        sourceName: 'Prompt',
                    })}
                    subline={i18n.translate({
                        id: 'tabs.knowledgebase.section.knowledgebase.subtitle',
                        sourceName: 'Prompt',
                    })}
                >
                    <SearchFilter
                        value={localQuery}
                        onChange={searchActions.setLocalQuery}
                        onApply={searchActions.applyQuery}
                        onClear={searchActions.clearQuery}
                        className="mb-6"
                    />

                    <PaginationWrapper
                        listType={PaginationListType.Knowledgebases}
                        itemsFunction={knowledgebaseService.getKnowledgebasesByPage}
                        itemsPerPage={12}
                        query={query ?? null}
                        renderItem={(knowledgebase: Knowledgebase) => (
                            <KnowledgebaseItem
                                key={knowledgebase.identifier}
                                knowledgebase={knowledgebase}
                                handleChangeFunction={() => {
                                    PromptActions.handleKnowledgebaseCheckboxChange({
                                        target: {
                                            checked: !prompt.knowledgebases.includes(knowledgebase.identifier),
                                            name: knowledgebase.identifier,
                                        },
                                    } as React.ChangeEvent<HTMLInputElement>);
                                }}
                                isActive={prompt.knowledgebases.includes(knowledgebase.identifier)}
                            />
                        )}
                        className="grid grid-cols-1 md:grid-cols-2 gap-6 my-3"
                    />
                </TabSection>
            )}
        </div>
    );
}

export default KnowledgebaseTab;

function KnowledgebaseItem({ knowledgebase, handleChangeFunction, isActive }: any) {
    return (
        <div
            className={`relative border-[2px] p-6 w-full overflow-hidden rounded-xl cursor-pointer hover:border-primary transition-all duration-150 ${
                isActive ? 'border-primary' : 'border-layout-borderColor'
            }`}
            onClick={() => handleChangeFunction(knowledgebase.identifier)}
        >
            <div>
                <p className="p-0 m-0 pr-5 mb-2 text-layout-textcolor text-wrap break-words w-full line-clamp-1">
                    {knowledgebase.name}
                </p>
                <span className="text-xs text-gray-500 line-clamp-2">{knowledgebase.description}</span>
            </div>

            {isActive && <CheckIcon />}
        </div>
    );
}
